import React, { useState, useRef } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
// import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useInView } from "react-intersection-observer";
import Featuressec1 from "../Features/Featuressec1";
import AboutSec4 from "../AboutUs/Aboutsec4";
import AboutSec5 from "../AboutUs/Aboutsec5";
import Contact from "../../pages/ContactUs/Contact";

/* ─── Particle Field ─────────────────────────────────────────────── */
const ParticleField = () => {
  const particles = Array.from({ length: 28 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 20 + 15,
    delay: Math.random() * 5,
  }));

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {particles.map((p) => (
        <motion.div
          key={p.id}
          animate={{
            x: [0, Math.random() * 60 - 30, Math.random() * 40 - 20, 0],
            y: [0, Math.random() * 60 - 30, Math.random() * 40 - 20, 0],
            opacity: [0.12, 0.35, 0.18, 0.12],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            ease: "linear",
            delay: p.delay,
          }}
          style={{
            position: "absolute",
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: "50%",
            background: "#407aff",
            top: `${p.y}%`,
            left: `${p.x}%`,
          }}
        />
      ))}
    </Box>
  );
};

/* ─── Animated Word ──────────────────────────────────────────────── */
const AnimatedWord = ({ children, delay = 0 }) => (
  <motion.span
    initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
    style={{ display: "inline-block" }}
  >
    {children}
  </motion.span>
);

/* ─── Scroll Progress Bar ────────────────────────────────────────── */
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: "linear-gradient(90deg, #407aff, #1a73e8, #6fa3ff)",
        transformOrigin: "0%",
        scaleX: scrollYProgress,
        zIndex: 9999,
      }}
    />
  );
};

/* ─── Magnetic Button ────────────────────────────────────────────── */
const MagneticButton = ({ children, onClick, sx = {} }) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  const handleMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setPos({
      x: (e.clientX - rect.left - rect.width / 2) * 0.15,
      y: (e.clientY - rect.top - rect.height / 2) * 0.15,
    });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setPos({ x: 0, y: 0 })}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: "spring", stiffness: 200, damping: 15 }}
    >
      <Button onClick={onClick} sx={sx}>
        {children}
      </Button>
    </motion.div>
  );
};

/* ─── Orbiting Visual ────────────────────────────────────────────── */
const OrbitVisual = () => {
  const nodes = [
    { label: "Data", color: "#407aff", size: 48, orbit: "88%", duration: 18, startAngle: 0 },
    { label: "ML",   color: "#1a73e8", size: 42, orbit: "78%", duration: 24, startAngle: 120 },
    { label: "API",  color: "#0d47a1", size: 38, orbit: "95%", duration: 30, startAngle: 240 },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: "280px", sm: "360px", md: "460px" },
        height: { xs: "280px", sm: "360px", md: "460px" },
        mx: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Outer dashed ring */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "1px dashed rgba(64,122,255,0.15)",
        }}
      />

      {/* Main rotating ring */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        style={{
          width: "82%",
          height: "82%",
          borderRadius: "50%",
          background: "linear-gradient(135deg,#f0f7ff 0%,#e8f1ff 100%)",
          border: "2px solid rgba(64,122,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 20px 60px rgba(64,122,255,0.12)",
          position: "absolute",
        }}
      >
        {/* Counter-rotating inner circle */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          style={{
            width: "72%",
            height: "72%",
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            boxShadow:
              "inset 0 2px 20px rgba(64,122,255,0.06), 0 8px 30px rgba(64,122,255,0.08)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            style={{ textAlign: "center" }}
          >
            <Typography
              sx={{
                color: "#407aff",
                fontWeight: 300,
                fontFamily: "Poppins",
                fontSize: { xs: "0.7rem", md: "0.9rem" },
                letterSpacing: "3px",
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Calebs
            </Typography>
            <Typography
              sx={{
                color: "#1a73e8",
                fontWeight: 700,
                fontFamily: "Poppins",
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                lineHeight: 1,
              }}
            >
              AI
            </Typography>
            <Typography
              sx={{
                color: "#546e7a",
                fontWeight: 400,
                fontFamily: "Poppins",
                fontSize: { xs: "0.6rem", md: "0.8rem" },
                letterSpacing: "2px",
                textTransform: "uppercase",
                mt: 0.5,
              }}
            >
              Platform
            </Typography>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Orbiting nodes */}
      {nodes.map((node, i) => (
        <motion.div
          key={i}
          animate={{ rotate: 360 }}
          transition={{ duration: node.duration, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            width: node.orbit,
            height: node.orbit,
            transform: `rotate(${node.startAngle}deg)`,
          }}
        >
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: node.duration, repeat: Infinity, ease: "linear" }}
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Box
              sx={{
                width: { xs: `${node.size * 0.72}px`, md: `${node.size}px` },
                height: { xs: `${node.size * 0.72}px`, md: `${node.size}px` },
                borderRadius: "12px",
                background: "#fff",
                border: `2px solid ${node.color}30`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 20px ${node.color}20`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  fontSize: { xs: "8px", md: "11px" },
                  color: node.color,
                  letterSpacing: "0.5px",
                }}
              >
                {node.label}
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      ))}

      {/* Pulse rings */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={`pulse-${i}`}
          animate={{ scale: [1, 1.8, 2.5], opacity: [0.15, 0.05, 0] }}
          transition={{ duration: 3, repeat: Infinity, delay: i * 1, ease: "easeOut" }}
          style={{
            position: "absolute",
            width: "30%",
            height: "30%",
            borderRadius: "50%",
            border: "1px solid #407aff",
            pointerEvents: "none",
          }}
        />
      ))}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════ */
const Homesec1 = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const handleGetStarted = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const { ref: preferredRef, inView: preferredInView } = useInView({ threshold: 0.2, triggerOnce: true });

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.3 } },
  };

  const staggerItem = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", damping: 20, stiffness: 100 } },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const heroStats = [
    { value: "99.9%", label: "Uptime" },
    { value: "3x",    label: "Faster" },
    { value: "24/7",  label: "Support" },
  ];

  const companies = [
    { name: "CI Dropshipping", delay: 0.3 },
    { name: "DataSync",        delay: 0.5 },
    { name: "Stockholm Magazine", delay: 0.7 },
  ];

  return (
    <Box sx={{ overflow: "hidden", backgroundColor: "#fff", width: "100%" }}>
      <ScrollProgress />

      {/* ══════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════ */}
      <Box
        component="section"
        id="hero"
        ref={heroRef}
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          alignItems: "center",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <ParticleField />

        {/* Background gradient orbs */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.06, 0.1, 0.06] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            width: "600px", height: "600px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(64,122,255,0.12) 0%, transparent 70%)",
            top: "-10%", right: "-10%",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.04, 0.08, 0.04] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 3 }}
          style={{
            position: "absolute",
            width: "500px", height: "500px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(26,115,232,0.1) 0%, transparent 70%)",
            bottom: "-5%", left: "-5%",
            pointerEvents: "none",
          }}
        />

        {/* Hero content wrapper with parallax */}
        <motion.div style={{ opacity: heroOpacity, y: heroY, width: "100%", position: "relative", zIndex: 2, mt:0 }}>
          <Container maxWidth="xl" sx={{ px: { xs: 3, sm: 4, md: 6, xl: 10 }, mt: 0 }}>
            <motion.div variants={staggerContainer} initial="hidden" animate="visible">

              {/* ── FLEX ROW: Text Left | Visual Right ── */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column-reverse", md: "row" },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: { xs: 6, md: 4 },
                  pt: 6, 
                  pb: { xs: 10, sm: 12, md: 14 },
                  minHeight: { md: "100vh" },
                }}
              >

                {/* ── LEFT COLUMN: All text content ── */}
                <Box
                  sx={{
                    flex: "1 1 55%",
                    maxWidth: { xs: "100%", md: "58%" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left" ,
                    pl: { xs: 0, md: 4, lg: 6 },
                  }}
                >
                  {/* Badge chip */}
                  <motion.div variants={staggerItem}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2.5,
                        py: 0.8,
                        mb: 3,
                        borderRadius: "50px",
                        background: "rgba(64,122,255,0.08)",
                        border: "1px solid rgba(64,122,255,0.18)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 7, height: 7,
                          borderRadius: "50%",
                          backgroundColor: "#407aff",
                          animation: "pulse 2s infinite",
                          "@keyframes pulse": {
                            "0%,100%": { opacity: 1, transform: "scale(1)" },
                            "50%": { opacity: 0.5, transform: "scale(1.4)" },
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "13px",
                          color: "#407aff",
                          letterSpacing: "0.5px",
                        }}
                      >
                        AI-Powered Platform
                      </Typography>
                    </Box>
                  </motion.div>

                  {/* Headline */}
                  <motion.div variants={staggerItem}>
                    <Typography
                      component="h1"
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        fontSize: { xs: "2.4rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                        color: "#0d1b3e",
                        lineHeight: 1.1,
                        mb: 0.5,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      <AnimatedWord delay={0.4}>Smarter</AnimatedWord>{" "}
                      <motion.span
                        initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                        style={{
                          display: "inline-block",
                          background: "linear-gradient(135deg, #407aff 0%, #1a73e8 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        Automations
                      </motion.span>
                    </Typography>
                  </motion.div>

                  <motion.div variants={staggerItem}>
                    <Typography
                      component="h1"
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        fontSize: { xs: "2.4rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                        color: "#0d1b3e",
                        lineHeight: 1.1,
                        mb: 3,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      <AnimatedWord delay={0.6}>Stronger</AnimatedWord>{" "}
                      <motion.span
                        initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.8, delay: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                        style={{
                          display: "inline-block",
                          background: "linear-gradient(135deg, #407aff 0%, #1a73e8 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        Teams
                      </motion.span>
                    </Typography>
                  </motion.div>

                  {/* Subtext */}
                  <motion.div variants={staggerItem}>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: { xs: "15px", sm: "16px", md: "17px" },
                        color: "#546e7a",
                        mb: 4.5,
                        maxWidth: "520px",
                        lineHeight: 1.85,
                        mx: { xs: "auto", md: 0 },
                      }}
                    >
                      Calebs provides a unified AI platform that transforms your data into actionable
                      insights, automates complex workflows, and delivers executive-grade decision
                      intelligence — all from a single, secure environment.
                    </Typography>
                  </motion.div>

                  {/* CTA Buttons */}
                  <motion.div variants={staggerItem}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2,
                        alignItems: { xs: "stretch", sm: "center" },
                        mb: 6,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <MagneticButton
                        onClick={handleGetStarted}
                        sx={{
                          py: 1.75,
                          px: 4.5,
                          background: "linear-gradient(135deg, #407aff 0%, #1a73e8 100%)",
                          color: "#fff",
                          fontSize: "15px",
                          fontWeight: 600,
                          borderRadius: "50px",
                          textTransform: "none",
                          fontFamily: "Poppins",
                          boxShadow: "0 8px 30px rgba(64,122,255,0.35)",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background: "linear-gradient(135deg, #305ec2 0%, #1557b0 100%)",
                            boxShadow: "0 14px 40px rgba(64,122,255,0.45)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        Get Started
                        <ArrowForwardIcon sx={{ fontSize: "18px" }} />
                      </MagneticButton>

                      <Button
                        onClick={() =>
                          document.getElementById("features")?.scrollIntoView({ behavior: "smooth", block: "start" })
                        }
                        sx={{
                          py: 1.75,
                          px: 4,
                          backgroundColor: "transparent",
                          color: "#407aff",
                          fontSize: "15px",
                          fontWeight: 600,
                          borderRadius: "50px",
                          textTransform: "none",
                          fontFamily: "Poppins",
                          border: "1.5px solid #d0e3ff",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#407aff",
                            backgroundColor: "rgba(64,122,255,0.05)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        See How It Works
                      </Button>
                    </Box>
                  </motion.div>

                  {/* Divider */}
                  <motion.div variants={staggerItem}>
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "420px" },
                        height: "1px",
                        background: "linear-gradient(90deg, rgba(64,122,255,0.25), transparent)",
                        mb: 3,
                        mx: { xs: "auto", md: 0 },
                      }}
                    />
                  </motion.div>

                  {/* Stats Row */}
                  <motion.div variants={staggerItem}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: { xs: 4, sm: 5 },
                        justifyContent: { xs: "center", md: "flex-start" },
                        flexWrap: "wrap",
                      }}
                    >
                      {heroStats.map((stat, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.3 + i * 0.15, duration: 0.5 }}
                        >
                          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                            <Typography
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 700,
                                fontSize: { xs: "1.4rem", md: "1.75rem" },
                                background: "linear-gradient(135deg, #407aff, #1a73e8)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                lineHeight: 1.2,
                              }}
                            >
                              {stat.value}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 400,
                                fontSize: "12px",
                                color: "#90a4ae",
                                letterSpacing: "1px",
                                textTransform: "uppercase",
                                mt: 0.25,
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  </motion.div>
                </Box>

                {/* ── RIGHT COLUMN: Orbit Visual ── */}
                <Box
                  sx={{
                    flex: "1 1 42%",
                    maxWidth: { xs: "340px", sm: "420px", md: "46%" },
                    mx: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.88, x: 30 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                    style={{ width: "100%" }}
                  >
                    <OrbitVisual />
                  </motion.div>
                </Box>

              </Box>
            </motion.div>
          </Container>
        </motion.div>
      </Box>

      {/* ══════════════════════════════════════
          PREFERRED BY SECTION
      ══════════════════════════════════════ */}
      <Box
        component="section"
        ref={preferredRef}
        sx={{
          backgroundColor: "#fafcff",
          py: { xs: 5, md: 6 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Top gradient border */}
        <Box
          sx={{
            position: "absolute",
            top: 0, left: "5%", right: "5%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, #d0e3ff, transparent)",
          }}
        />
        {/* Bottom gradient border */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0, left: "5%", right: "5%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, #d0e3ff, transparent)",
          }}
        />

        <Container maxWidth="lg">
          {/* Label */}
          <motion.div
            initial="hidden"
            animate={preferredInView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 3,
              }}
            >
              {/* Left line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={preferredInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
                style={{ transformOrigin: "right", flex: 1, maxWidth: 100 }}
              >
                <Box sx={{ height: "1px", background: "linear-gradient(90deg, transparent, #c5d8f8)" }} />
              </motion.div>

              <Typography
                sx={{
                  textAlign: "center",
                  color: "#90a4ae",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: { xs: "10px", sm: "12px" },
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                Preferred by engineering leaders
              </Typography>

              {/* Right line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={preferredInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
                style={{ transformOrigin: "left", flex: 1, maxWidth: 100 }}
              >
                <Box sx={{ height: "1px", background: "linear-gradient(90deg, #c5d8f8, transparent)" }} />
              </motion.div>
            </Box>
          </motion.div>

          {/* Company names flex row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0, sm: 2, md: 3 },
            }}
          >
            {companies.map((company, index) => (
              <React.Fragment key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={
                    preferredInView
                      ? { opacity: 1, y: 0, transition: { duration: 0.5, delay: company.delay } }
                      : {}
                  }
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      px: { xs: 2, md: 3 },
                      py: { xs: 0.8, sm: 1 },
                      cursor: "default",
                      "&:hover .co-name": { color: "#407aff" },
                    }}
                  >
                    <Typography
                      className="co-name"
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 600,
                        color: "#b0bec5",
                        fontSize: { xs: "17px", sm: "19px", md: "22px" },
                        transition: "color 0.3s ease",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {company.name}
                    </Typography>
                  </Box>
                </motion.div>

                {/* Separator dot */}
                {index < companies.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={
                      preferredInView
                        ? { opacity: 1, scale: 1, transition: { duration: 0.4, delay: company.delay + 0.15 } }
                        : {}
                    }
                  >
                    <Box
                      sx={{
                        display: { xs: "none", sm: "block" },
                        width: 5, height: 5,
                        borderRadius: "50%",
                        backgroundColor: "#d0e3ff",
                        flexShrink: 0,
                      }}
                    />
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ══════════════════════════════════════
          FEATURES
      ══════════════════════════════════════ */}
      <Box component="section" id="features" sx={{ backgroundColor: "#fff" }}>
        <Featuressec1 />
      </Box>

      {/* ══════════════════════════════════════
          WHY CALEBS
      ══════════════════════════════════════ */}
      <Box component="section" id="why-calebs" sx={{ backgroundColor: "#fff" }}>
        <AboutSec4 />
      </Box>

      {/* ══════════════════════════════════════
          INTEGRATION
      ══════════════════════════════════════ */}
      <Box component="section" id="integration" sx={{ backgroundColor: "#fff" }}>
        <AboutSec5 />
      </Box>

      {/* ══════════════════════════════════════
          CONTACT
      ══════════════════════════════════════ */}
      <Box component="section" id="contact" sx={{ backgroundColor: "#fff" }}>
        <Contact />
      </Box>
    </Box>
  );
};

export default Homesec1;
